import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDidUpdate } from '@toss/react';
import { useDelayedLoading } from '@/hooks';
import { cn } from '@/utils/tailwind.util';
import { LIST_GRID_CLASSNAME } from './listGrid';
type InfiniteListViewProps<T> = {
  items: T[] | undefined;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
  EmptyComponent?: React.ReactNode;
  ErrorComponent?: React.ReactNode;
  LoadingComponent?: React.ReactNode;
  HeaderComponent?: React.ReactNode;
  className?: string;
};
const DEFAULT_LOADING_COMPONENT = <>Loading...</>;

/**
 * 목록 그리드 규약. 내 퀴즈 한 화면은 폴더·퀴즈 두 섹션이 **같은 열 폭**으로 이어져야
 * 하는데(시안 33390:92793) 폴더 섹션은 무한 스크롤이 아니라 4칸 미리보기라 이
 * 컴포넌트를 쓰지 않는다. 그래서 열 규칙만 따로 내보내 두 곳이 공유한다.
 */
/** 열 규칙은 `./listGrid` 가 소유한다. 기존 import 경로를 지키려고 여기서 다시 내보낸다. */
export { LIST_GRID_CLASSNAME };
export function InfiniteListView<T>({
  items,
  isLoading,
  isError,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage = false,
  renderItem,
  EmptyComponent = null,
  ErrorComponent = null,
  LoadingComponent = DEFAULT_LOADING_COMPONENT,
  HeaderComponent = null,
  className = ''
}: InfiniteListViewProps<T>) {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: false,
    threshold: 0.1
  });
  const delayedLoading = useDelayedLoading(isLoading, 2000);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  useEffect(() => {
    isFetchingNextPageRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);
  useDidUpdate(() => {
    if (inView && hasNextPage && !isFetchingNextPageRef.current) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);
  if (isLoading || !items) return null;
  if (delayedLoading) return <>{LoadingComponent}</>;
  if (isError) return <>{ErrorComponent}</>;
  if (items.length === 0 && !hasNextPage) return <>{EmptyComponent}</>;
  return <div className={cn(LIST_GRID_CLASSNAME, 'pb-lg', className)} data-sentry-component="InfiniteListView" data-sentry-source-file="InfiniteListView.tsx">
      {HeaderComponent}
      {items.map((item, idx) => renderItem(item, idx))}
      <div key="in-view-trigger" ref={ref} />
    </div>;
}